import React from "react";
import { Bell, CheckCheck, LoaderCircle, Square } from "lucide-react";
import { useNotifications } from "../hooks/useNotifications.jsx";
import { useGeotabLiveAlerts } from "./GeotabLiveAlerts.jsx";
import GeotabLiveAlertsJournal from "./GeotabLiveAlertsJournal.jsx";

const dateFormatter = new Intl.DateTimeFormat("fr-CA", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatNotificationDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : dateFormatter.format(date);
}

export default function NotificationCenterButton() {
  const {
    errorMessage,
    isLoading,
    markAllRead,
    markRead,
    notifications,
    refresh,
    unreadCount,
  } = useNotifications();
  const [isOpen, setIsOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  // Avis Geotab en direct : un onglet de plus, et « Tout voir » des cartes ouvre la cloche dessus.
  const live = useGeotabLiveAlerts();
  const liveOn = Boolean(live?.enabled);
  const liveUnread = live?.unreadCount || 0;
  const [tab, setTab] = React.useState("notifications");
  const showLive = liveOn && tab === "live";
  const totalUnread = unreadCount + liveUnread;
  React.useEffect(() => {
    if (!live?.journalRequest) return;
    setTab("live");
    setIsOpen(true);
  }, [live?.journalRequest]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const openNotification = async (notification) => {
    if (!notification.read_at) {
      await markRead(notification.id, true).catch(() => {});
    }
    if (notification.action_url) {
      setIsOpen(false);
      window.location.assign(notification.action_url);
    }
  };

  return (
    <div ref={rootRef} className="relative shrink-0 overflow-visible" data-notification-center="true">
      <button
        type="button"
        className="relative inline-flex min-h-[30px] min-w-[38px] shrink-0 items-center justify-center overflow-visible rounded-[4px] border border-slate-300 bg-white px-2 text-slate-700 shadow-header-action transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/60"
        aria-label={`Notifications${totalUnread ? `, ${totalUnread} non lue${totalUnread > 1 ? "s" : ""}` : ""}`}
        aria-expanded={isOpen}
        onClick={() => {
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            void refresh({ showNewToasts: false });
            if (liveUnread > 0 && unreadCount === 0) setTab("live");
          }
        }}
      >
        <Bell className={`h-4 w-4 ${unreadCount > 0 ? "text-danger motion-safe:animate-pulse motion-reduce:animate-none" : "text-slate-700"}`} />
        {totalUnread > 0 ? (
          <span className={`absolute -right-1 -top-1 inline-flex min-h-4 min-w-4 items-center justify-center rounded-[4px] px-1 text-[9px] font-black leading-none text-white ring-2 ring-white ${unreadCount > 0 ? "bg-danger" : "bg-[#3f5585]"}`}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <section role="dialog" aria-modal="false" className={`absolute right-0 top-[calc(100%+0.55rem)] z-[calc(var(--z-index-modal)+8)] flex max-h-[min(34rem,calc(100vh-7rem))] ${liveOn ? "w-[min(23rem,calc(100vw-1.5rem))]" : "w-[min(20rem,calc(100vw-1.5rem))]"} flex-col overflow-hidden whitespace-normal rounded-[4px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.22)]`} aria-label="Centre de notifications">
          <header className={`flex items-center justify-between gap-3 px-4 py-3 ${liveOn ? "pb-0" : "border-b border-slate-100"}`}>
            <div>
              <p className="m-0 text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Boîte de réception</p>
              <h2 className="m-0 mt-0.5 text-sm font-bold text-slate-900">Notifications</h2>
            </div>
            {showLive ? null : (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={unreadCount === 0}
                onClick={() => void markAllRead()}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Tout lire
              </button>
            )}
          </header>
          {liveOn ? (
            <div role="tablist" aria-label="Boîte de réception" className="flex gap-4 border-b border-slate-200 px-4 pt-2.5 text-xs font-extrabold text-slate-500">
              {[["notifications", "Notifications", unreadCount], ["live", "Arrivées et départs", liveUnread]].map(([key, label, count]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  className={`inline-flex items-center gap-1.5 pb-2 ${tab === key ? "text-slate-900 shadow-[inset_0_-2px_0_var(--color-accent)]" : "hover:text-slate-700"}`}>
                  {label}
                  {count ? <span className={`min-w-4 rounded-[4px] px-1 text-center text-[9.5px] leading-4 text-white ${key === "live" ? "bg-[#3f5585]" : "bg-danger"}`}>{count}</span> : null}
                </button>
              ))}
            </div>
          ) : null}

          {showLive ? <GeotabLiveAlertsJournal live={live} onOpen={(event) => { setIsOpen(false); live.showCard(event); }} /> : <div className="min-h-24 overflow-x-hidden overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="grid min-h-28 place-items-center text-slate-500">
                <LoaderCircle className="h-5 w-5 animate-spin" />
              </div>
            ) : errorMessage && notifications.length === 0 ? (
              <p className="m-0 px-4 py-6 text-sm leading-6 text-red-700">{errorMessage}</p>
            ) : notifications.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <Bell className="mx-auto h-6 w-6 text-slate-300" />
                <p className="m-0 mt-2 text-sm font-semibold text-slate-700">Aucune notification</p>
                <p className="m-0 mt-1 text-xs leading-5 text-slate-500">Les rappels et résultats de tâches apparaîtront ici.</p>
              </div>
            ) : notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={`flex min-w-0 w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50 ${notification.read_at ? "bg-white" : "bg-slate-50"}`}
                onClick={() => void openNotification(notification)}
              >
                <Square className={`mt-1 h-2.5 w-2.5 shrink-0 ${notification.read_at ? "fill-slate-200 text-slate-200" : "fill-accent text-accent"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-[13px] font-bold leading-5 text-slate-900">{notification.title}</span>
                  <span className="mt-0.5 block break-words text-[12px] leading-5 text-slate-600">{notification.body}</span>
                  <span className="mt-1.5 block text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{formatNotificationDate(notification.created_at)}</span>
                </span>
              </button>
            ))}
          </div>}
        </section>
      ) : null}
    </div>
  );
}
